import { FlakinessReport } from '@flakiness/flakiness-report';
import { Multimap } from '@flakiness/shared/common/multimap.js';
import mime from 'mime';
import { ImageDiff } from './components/image-diff-attachment.js';

export type Artifact = {
  fileName: string,
  url: string,
  viewerUrl?: string,
  contentType: string,
  attachmentName: string,
}

export function classifyArtifacts(someArtifacts: Iterable<Artifact>): ClassifiedArtifacts {
  // Since we will mutate attachments later on, clone it real quick to protect callsite.
  const artifacts = Array.from(someArtifacts);
  // First of all, let's sort all attachments by name.
  artifacts.sort((a1, a2) => a1.fileName < a2.fileName ? -1 : 1);

  const videos = extractArray(artifacts, artifact => artifact.contentType.startsWith('video/'));
  const traces = extractArray(artifacts, artifact => artifact.attachmentName === 'trace');
  const screenshots = extractArray(artifacts, artifact => artifact.contentType.startsWith('image/'));
  const misc = artifacts;

  // Walk over all screenshots and assign them to image diffs.
  const diffScreenshots = new Map<string, Artifact>();
  const expectationScreenshots = new Multimap<string, {
    category: string,
    screenshot: Artifact,
  }>();

  for (const screenshot of screenshots) {
    const match = screenshot.fileName.match(/^(.*)-(expected|actual|diff|previous)(\.[^.]+)?$/);
    if (!match)
      continue;
    const [, name, category, ext = ''] = match;
    const diffName = name + ext;
    if (category === 'diff')
      diffScreenshots.set(diffName, screenshot);
    else
      expectationScreenshots.set(diffName, { category, screenshot });
  }
  // Create all image diffs. Each should have a "diff" image and exactly 2 expectation screenshots.
  const screenshotsInImageDiffs = new Set<Artifact>();
  const imageDiffs: ImageDiff[] = [...expectationScreenshots].map(([name, expectations]) => {
    let [e1, e2] = [...expectations];
    if (!e1 || !e2)
      return [];
    if (e1.category > e2.category)
      [e1, e2] = [e2, e1];
    const diff = diffScreenshots.get(name);
    if (diff)
      screenshotsInImageDiffs.add(diff);
    screenshotsInImageDiffs.add(e1.screenshot);
    screenshotsInImageDiffs.add(e2.screenshot);
    return {
      name,
      diff,
      expectation1: {
        ...e1.screenshot,
        category: e1.category,
      },
      expectation2: {
        ...e2.screenshot,
        category: e2.category,
      },
    } satisfies ImageDiff;
  }).flat();
  extractArray(screenshots, screenshot => screenshotsInImageDiffs.has(screenshot));

  // Finally, before returning all these attachments, let's sort each group.
  // Most of the groups should be sorted already since we sorted the attachments initially; however,
  // ImageDiffs are not.
  imageDiffs.sort((diff1, diff2) => diff1.name < diff2.name ? -1 : 1);
  return { videos, traces, screenshots, imageDiffs, misc }
}

// Like filtering, but also extracting elements from the array.
function extractArray<T>(array: T[], predicate: (t: T) => boolean) {
  const result: T[] = [];
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate(array[i])) {
      result.push(array[i]);
      array.splice(i, 1);
    }
  }
  return result;
} 

export type ClassifiedArtifacts = {
  videos: Artifact[],
  traces: Artifact[],
  screenshots: Artifact[],
  imageDiffs: ImageDiff[],
  misc: Artifact[],
}

export type AttachmentUrlBuilder = (attachmentId: FlakinessReport.AttachmentId, isPlaywrightTrace?: boolean) => string;

export function artifactFromAttachment(attachment: FlakinessReport.Attachment, urlBuilder: AttachmentUrlBuilder): Artifact {
  const fileName = attachment.name.includes('.') ? attachment.name : attachment.name + '.' + mime.getExtension(attachment.contentType);
  const isPlaywrightTrace = attachment.name === 'trace';
  return {
    attachmentName: attachment.name,
    contentType: attachment.contentType,
    url: urlBuilder(attachment.id, false),
    viewerUrl: isPlaywrightTrace ? urlBuilder(attachment.id, true) : undefined,
    fileName,
  };
}
